import type { PlatformWorkbookBucketId } from '../ApplicationWorkbook/workbook.types'

/** Platform workbook buckets — EZOS template sections (default homes). */
export const WORKBOOK_BUCKETS = [
    {
        id: 'identity_contact',
        label: 'Identity & Contact',
        summary:
            'Legal name, gender, contact info, current address, and school.',
    },
    {
        id: 'lease_preferences',
        label: 'Lease Preferences',
        summary: 'Move-in, move-out, floor plan, premiums, and roommates.',
    },
    {
        id: 'financial',
        label: 'Qualification',
        summary:
            'How the applicant will qualify, plus guarantor identity if they choose a guarantor.',
    },
    {
        id: 'failed_qualification',
        label: 'Failed Qualification',
        summary:
            'After-fail qualifying methods when the applicant chooses among them.',
    },
    {
        id: 'operational',
        label: 'Add-Ons & Emergency Contacts',
        summary: 'Emergency contact, parking, and pets.',
    },
    {
        id: 'lifestyle',
        label: 'Lifestyle & Roommates',
        summary: 'Roommate compatibility and living preferences.',
    },
] as const

export type WorkbookBucketId = PlatformWorkbookBucketId | string

export const MAX_CUSTOM_SECTIONS = 5

export const PLATFORM_BUCKET_IDS: readonly PlatformWorkbookBucketId[] = WORKBOOK_BUCKETS.map(
    (b) => b.id,
)

/** Soft default: identity before entering Screening. */
export const DEFAULT_SCREENING_REQUIRED_BUCKETS: WorkbookBucketId[] = [
    'identity_contact',
]

export function isPlatformBucketId(id: string): id is PlatformWorkbookBucketId {
    return (PLATFORM_BUCKET_IDS as readonly string[]).includes(id)
}

export function isCustomSectionId(id: string): boolean {
    return id.startsWith('custom_') && !isPlatformBucketId(id) && id.length > 7
}

/** Accept platform ids and custom_* section ids. */
export function asBucketIdList(value: unknown): WorkbookBucketId[] {
    if (!Array.isArray(value)) return []
    return value.filter(
        (item): item is WorkbookBucketId =>
            typeof item === 'string' &&
            (isPlatformBucketId(item) || isCustomSectionId(item)),
    )
}

export function toggleBucket(
    current: WorkbookBucketId[],
    bucketId: WorkbookBucketId,
    enabled: boolean,
): WorkbookBucketId[] {
    if (enabled) {
        if (current.includes(bucketId)) return current
        return [...current, bucketId]
    }
    return current.filter((id) => id !== bucketId)
}

export function platformBucketLabel(bucketId: string): string {
    return (
        WORKBOOK_BUCKETS.find((bucket) => bucket.id === bucketId)?.label ??
        bucketId
    )
}

export function platformBucketSummary(bucketId: string): string {
    return (
        WORKBOOK_BUCKETS.find((bucket) => bucket.id === bucketId)?.summary ?? ''
    )
}
